import React, { useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldErrorMessage, FormFieldGroup } from "@components/Forms/Fields";
import { DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { url_reverse } from "@components/Utils";
import { isGm3 } from "@vkplay/shared";

const API_BASE_URL = isGm3 ? "https://kravchenko-api.gm3.dev-vkplay.ru" : "https://api.vkplay.ru";

const ConfirmRemoveModal = ({ serverId, show, handleClose, searchFormRef }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { api_post } = useRequest();

    const confirmHandle = () => {
        setIsLoading(true);
        // TODO: поправить на useRequest
        fetch(`${API_BASE_URL}/messenger/admin/servers/${serverId}`, {
            method: "DELETE",
            credentials: "include",
        })
            .then((response) => {
                if (response.status !== 200) {
                    alert(t("Ошибка"));
                }
                api_post(null, url_reverse("api:delete_chat_id"), { params: { chat_id: serverId } });
            })
            .catch((error) => alert(error))
            .finally(() => {
                setIsLoading(false);
                handleClose();
                searchFormRef.current.requestSubmit();
            });
    };

    return (
        <DevModal
            show={show}
            onHide={handleClose}
            header={t("Подтверждение действия")}
            body={t("Вы действительно хотите удалить сервер?")}
            footer={
                <>
                    <DevForm.Button isLoading={isLoading} variant="danger" name="delete" onClick={confirmHandle}>
                        {t("Удалить")}
                    </DevForm.Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};

const ConfirmToggleOfficialModal = ({ serverId, show, handleClose, isOfficial, searchFormRef }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const confirmHandle = () => {
        setIsLoading(true);
        // TODO: поправить на useRequest
        fetch(`${API_BASE_URL}/messenger/admin/servers/${serverId}`, {
            method: "PATCH",
            credentials: "include",
            body: JSON.stringify({ isOfficial }),
        })
            .then((response) => {
                if (response.status !== 200) {
                    alert(t("Ошибка"));
                }
            })
            .catch((error) => alert(error))
            .finally(() => {
                setIsLoading(false);
                handleClose(false);
                searchFormRef.current.requestSubmit();
            });
    };

    return (
        <DevModal
            show={show}
            onHide={handleClose}
            header={t("Подтверждение действия")}
            body={
                isOfficial
                    ? t("Вы действительно хотите сделать сервер неофициальным?")
                    : t("Вы действительно хотите сделать сервер официальным?")
            }
            footer={
                <>
                    <DevForm.Button isLoading={isLoading} variant="primary" name="confirm" onClick={confirmHandle}>
                        {t("Подтвердить")}
                    </DevForm.Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};

const SearchForm = ({ setData, searchFormRef }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [validated, setValidated] = useState(false);

    const handleSubmitForm = (event) => {
        const form = event.currentTarget;

        event.preventDefault();
        event.stopPropagation();

        setValidated(true);

        if (form.checkValidity() === true) {
            setIsLoading(true);
            // TODO: нужна ли пагинация?
            // TODO: поправить на useRequest
            fetch(`${API_BASE_URL}/messenger/admin/servers?search=${search}&limit=100`, {
                credentials: "include",
            })
                .then((response) => {
                    if (response.status === 200) {
                        return response.json();
                    }
                    alert(t("Ошибка"));
                })
                .then((servers) => setData(servers?.length > 0 ? servers : false))
                .catch((error) => alert(error))
                .finally(() => setIsLoading(false));
        }
    };

    return (
        <Form noValidate validated={validated} onSubmit={handleSubmitForm} ref={searchFormRef}>
            <FormFieldGroup label={t("Поиск сервера")} required={true}>
                <DevForm.TextInput onChange={(e) => setSearch(e.target.value)} value={search} required />
                <FormFieldErrorMessage error={t("Обязательное поле.")} />
            </FormFieldGroup>
            <FormFieldGroup>
                <DevForm.Button isLoading={isLoading} type="submit" name="search">
                    {t("Найти")}
                </DevForm.Button>
            </FormFieldGroup>
        </Form>
    );
};

const SearchResult = ({ data, searchFormRef }) => {
    const { t } = useTranslation();
    const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false);
    const [showConfirmMakeOfficialModal, setShowConfirmMakeOfficialModal] = useState(false);
    const [serverId, setServerId] = useState();
    const [isOfficial, setIsOfficial] = useState();

    const removeServerHandle = (id) => {
        setServerId(id);
        setShowConfirmRemoveModal(true);
    };

    const toggleOfficialHandle = (id, iso) => {
        setServerId(id);
        setIsOfficial(!iso);
        setShowConfirmMakeOfficialModal(true);
    };

    if (data === false) {
        return (
            <Alert variant="warning" className="mt-5">
                {t("Ничего не найдено")}
            </Alert>
        );
    }

    if (data.length > 0) {
        return (
            <Table className="mt-5">
                <thead>
                    <tr>
                        <th width="100px">#</th>
                        <th width="250px">ID</th>
                        <th>{t("Название")}</th>
                        <th width="250px" />
                        <th width="380px" />
                    </tr>
                </thead>
                <tbody>
                    {data.map((server, index) => (
                        <tr key={server.id}>
                            <td>{++index}</td>
                            <td>{server.id}</td>
                            <td>{server.name}</td>
                            <td>
                                <Badge variant={server.isOfficial ? "success" : "secondary"} className="mr-2">
                                    {server.isOfficial ? t("Официальный") : t("Не официальный")}
                                </Badge>
                                <Badge variant={server.isPrivate ? "primary" : "secondary"}>
                                    {server.isPrivate ? t("Приватный") : t("Не приватный")}
                                </Badge>
                            </td>
                            <td className="text-right">
                                <Button variant="danger" className="mr-3" onClick={() => removeServerHandle(server.id)}>
                                    {t("Удалить")}
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => toggleOfficialHandle(server.id, server.isOfficial)}
                                >
                                    {server.isOfficial ? t("Сделать неофициальным") : t("Сделать официальным")}
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>

                <ConfirmRemoveModal
                    serverId={serverId}
                    show={showConfirmRemoveModal}
                    handleClose={() => setShowConfirmRemoveModal(false)}
                    searchFormRef={searchFormRef}
                />

                <ConfirmToggleOfficialModal
                    serverId={serverId}
                    show={showConfirmMakeOfficialModal}
                    handleClose={() => setShowConfirmMakeOfficialModal(false)}
                    isOfficial={isOfficial}
                    searchFormRef={searchFormRef}
                />
            </Table>
        );
    }

    return <></>;
};

export default function ModerateChatScreen() {
    const [data, setData] = useState([]);
    const { t } = useTranslation();
    const title = t("Модерация VK Play чата");
    const searchFormRef = useRef(null);

    return (
        <DefaultLayout>
            <Card>
                <Card.Header>{title}</Card.Header>
                <Card.Body>
                    <SearchForm setData={setData} searchFormRef={searchFormRef} />
                    <SearchResult data={data} searchFormRef={searchFormRef} />
                </Card.Body>
            </Card>
        </DefaultLayout>
    );
}
